import { useState } from 'react';
import { Button, Field, Modal, Spinner } from '../ui';
import { contentApi, apiErrorMessage } from '../../services/api';
import { useOrg } from '../../context/OrgContext';
import { useToast } from '../../context/ToastContext';
import useExamHierarchy from '../../hooks/useExamHierarchy';
import { topicChoices, anchorPayload, NO_TOPIC } from '../../utils/contentAnchor';

const CONTENT_TYPES = [
  ['notes_pdf', 'Notes PDF'],
  ['prepgen_notes', 'Prepgen.ai notes (customized)'],
  ['video_lesson', 'Video lesson']
];

export default function ContentDialog({ batches, exams = [], onClose, onPublished }) {
  const org = useOrg();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    type: 'notes_pdf',
    scope: 'all',
    storageUrl: '',
    examId: '',
    topicId: NO_TOPIC
  });

  // Only loaded once an exam is chosen; the hook caches per exam across dialogs.
  const { sections, loading: topicsLoading } = useExamHierarchy(form.examId || null);
  const choices = topicChoices(sections);

  const handlePublish = async () => {
    setSaving(true);
    try {
      await contentApi.publish(org.id, {
        title: form.title.trim(),
        type: form.type,
        scope: form.scope,
        ...(form.storageUrl.trim() && { storageUrl: form.storageUrl.trim() }),
        ...anchorPayload(form.examId, form.topicId, sections)
      });
      toast.success('Learning content published.');
      onPublished();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to publish content'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Publish learning content" onClose={onClose}>
      <div className="form">
        <Field label="Title">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <Field label="Content type">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {CONTENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Audience">
          <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
            <option value="all">All students</option>
            {batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>

        {/* Pinning to a topic puts the item beside that topic's notes in the
            student app. Left blank, it stays a course-wide resource. */}
        <Field
          label="Exam (optional)"
          hint="Pin this to a topic so students find it while they study that topic."
        >
          <select
            value={form.examId}
            onChange={(e) => setForm({ ...form, examId: e.target.value, topicId: NO_TOPIC })}
          >
            <option value="">Not tied to an exam</option>
            {exams.map((x) => (
              <option key={x.examId || x.id} value={x.examId || x.id}>{x.title}</option>
            ))}
          </select>
        </Field>
        {form.examId && (
          <Field label="Topic (optional)">
            {topicsLoading ? (
              <div className="page-loading"><Spinner /> Loading topics…</div>
            ) : (
              <select
                value={form.topicId}
                onChange={(e) => setForm({ ...form, topicId: e.target.value })}
              >
                <option value={NO_TOPIC}>Whole course</option>
                {choices.map((choice) => (
                  <option key={choice.topicId} value={choice.topicId}>{choice.label}</option>
                ))}
              </select>
            )}
          </Field>
        )}

        <Field label="File / video URL (optional)">
          <input placeholder="https://…" value={form.storageUrl} onChange={(e) => setForm({ ...form, storageUrl: e.target.value })} />
        </Field>
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.title.trim() || saving} onClick={handlePublish}>
            {saving ? 'Publishing…' : 'Publish'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
